/**
 * EventsCardList Section Component
 *
 * Mobile replacement for the events table — renders each event as a stacked
 * card (mirrors OrdersCardList — GRAIN-2340). Shown below the `md` breakpoint;
 * the table remains the desktop rendering.
 */

// React
import { memo, useEffect, useRef, type KeyboardEvent } from 'react';

// shadcn/ui
import { Avatar } from '@/components/ui/avatar';

// Shared components
import { Bar } from '../../Skeleton/primitives';
import { ChevronIcon, EmptySkuIcon } from '@components/icons';
import { MobileIconButton } from '../../layouts/MobileButtons';
import { EventStatusBadge } from '../pages/AllEvents/eventStatusBadge';

// Hooks
import { useTranslation } from '@hooks/useTranslation';

// Utils
import { cn } from '@/lib/utils';
import { formatTime } from '../../../utils/formatters';
import { formatEventDeliveryLabel } from '../deliveryLabel';
import { isPlaceholderPostalCode } from '../../../utils/postalCode';

// Types
import type { EventListItem, EventsCardListProps } from '../types';

export type { EventsCardListProps };

const SKELETON_CARD_COUNT = 3;

/** Fallback shown for pickup/service times that aren't set, mirroring the desktop columns' em-dash. */
const MISSING_TIME_PLACEHOLDER = '—';

interface EventCardProps {
  event: EventListItem;
  onEventIdClick?: (orderId: number) => void;
}

export const EventCard = memo(function EventCard({ event, onEventIdClick }: EventCardProps) {
  const { t } = useTranslation();

  const contactParts = [event.contactName, event.contactPhone, event.organisationName].filter(Boolean);
  const hasSalesperson = Boolean(event.salesperson?.name);
  const orderId = event.orderId;
  const handleOpen = onEventIdClick && orderId != null ? () => onEventIdClick(orderId) : undefined;
  const cardClassName = cn(
    'flex w-full flex-col gap-4 rounded-input border border-dinnerplate-white bg-dinnerplate-white-lighter p-4 text-left',
    handleOpen &&
      'cursor-pointer touch-manipulation hover:bg-dinnerplate-white-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-off-black/30',
  );

  const handleCardKeyDown = (e: KeyboardEvent) => {
    if (!handleOpen) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  const cardBody = (
    <>
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <span className="font-inter text-sm leading-normal font-semibold text-off-black whitespace-nowrap">
            {event.eventIdentifier}
          </span>
          <EventStatusBadge status={event.eventStatus} />
        </div>
        <span className="font-inter text-xs leading-tight-sm text-off-black">
          {event.salesOrderNumber}
        </span>
      </div>

      <div className="flex min-w-0 flex-col gap-1.5">
        <span className="font-inter text-sm leading-normal text-off-black">
          {event.eventType} · {event.guestCount} guests
        </span>
        <div className="flex items-center gap-3 font-inter text-xs leading-tight-sm text-off-black">
          <span>{event.eventDateFormatted}</span>
          <span>
            <span className="font-semibold">P:</span>{' '}
            {event.pickupTime ? formatTime(event.pickupTime) : MISSING_TIME_PLACEHOLDER}
          </span>
          <span>
            <span className="font-semibold">S:</span>{' '}
            {event.serviceTime ? formatTime(event.serviceTime) : MISSING_TIME_PLACEHOLDER}
          </span>
        </div>
      </div>

      <div className="h-px w-full bg-dinnerplate-white" />

      <div className="flex min-w-0 flex-col gap-1.5">
        <span className={cn(
          'font-inter text-sm leading-normal font-semibold',
          isPlaceholderPostalCode(event.postalCode) ? 'text-chilli-red' : 'text-off-black',
        )}>
          {formatEventDeliveryLabel(event, t)}
        </span>
        {contactParts.length > 0 ? (
          <span className="font-inter text-xs leading-tight-sm text-off-black">
            {contactParts.join(' · ')}
          </span>
        ) : (
          <span className="font-inter text-xs leading-tight-sm text-grey">-</span>
        )}
      </div>

      {hasSalesperson ? (
        <div className="flex items-center gap-3">
          <Avatar size="lg" src={event.salesperson.avatarUrl ?? undefined} name={event.salesperson.name} />
          <span className="truncate font-inter text-sm leading-normal font-semibold text-off-black">
            {event.salesperson.name}
          </span>
        </div>
      ) : (
        <span className="font-inter text-sm leading-normal text-grey">-</span>
      )}
    </>
  );

  if (handleOpen) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={cardClassName}
        onClick={handleOpen}
        onKeyDown={handleCardKeyDown}
      >
        {cardBody}
      </div>
    );
  }

  return <div className={cardClassName}>{cardBody}</div>;
});

function SkeletonCard() {
  return (
    <div className="flex w-full flex-col gap-4 rounded-input border border-dinnerplate-white bg-dinnerplate-white-lighter p-4">
      <div className="flex items-center justify-between gap-2">
        <Bar className="h-4 w-32 rounded" />
        <Bar className="h-6 w-24 rounded" />
      </div>
      <Bar className="h-4 w-3/4 rounded" />
      <div className="h-px w-full bg-dinnerplate-white" />
      <Bar className="h-4 w-full rounded" />
      <div className="flex items-center gap-3">
        <Bar className="size-12 rounded-full" />
        <Bar className="h-4 w-24 rounded" />
      </div>
    </div>
  );
}

export function EventsCardList({
  events,
  isLoading = false,
  isFetching = false,
  onEventIdClick,
  pagination,
  className,
}: EventsCardListProps) {
  const { t } = useTranslation();
  const showSkeleton = isLoading && events.length === 0;

  // The list is its own scroll container, so a page change would otherwise
  // leave the user at the bottom (where the pager sits) of the new page.
  const scrollRef = useRef<HTMLDivElement>(null);
  const page = pagination?.page;
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [page]);

  return (
    <div
      ref={scrollRef}
      className={cn(
        'flex flex-col gap-3 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]',
        isFetching && !showSkeleton && 'opacity-70 transition-opacity duration-200 ease-out motion-reduce:transition-none',
        className
      )}
    >
      {showSkeleton ? (
        Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => <SkeletonCard key={index} />)
      ) : events.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center py-16">
          <EmptySkuIcon className="mb-4 w-16 h-16 text-grey" />
          <p className="font-inter text-sm text-grey">{t('card_list.no_results')}</p>
        </div>
      ) : (
        events.map((event) => (
          <EventCard key={event.id} event={event} onEventIdClick={onEventIdClick} />
        ))
      )}
      {!showSkeleton && events.length > 0 && pagination && pagination.totalPages > 1 && (
        <nav
          className="flex shrink-0 items-center justify-between pt-1"
          aria-label={t('card_list.pagination_aria')}
        >
          <MobileIconButton
            aria-label={t('card_list.previous_page_aria')}
            disabled={pagination.page <= 1}
            onClick={() => pagination.onPageChange(pagination.page - 1)}
            className="disabled:opacity-50"
          >
            <ChevronIcon direction="left" />
          </MobileIconButton>
          <span className="font-inter text-sm leading-normal text-grey tabular-nums">
            {t('card_list.page_label')} {pagination.page} {t('card_list.of_label')} {pagination.totalPages}
          </span>
          <MobileIconButton
            aria-label={t('card_list.next_page_aria')}
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => pagination.onPageChange(pagination.page + 1)}
            className="disabled:opacity-50"
          >
            <ChevronIcon direction="right" />
          </MobileIconButton>
        </nav>
      )}
    </div>
  );
}
